/* eslint-disable @typescript-eslint/promise-function-async */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog as MuiDialog, DialogTitle as MuiDialogTitle, DialogContent as MuiDialogContent, DialogActions as MuiDialogActions, Grow, } from '@mui/material';
import { clsx } from 'clsx';
import { IconCloseMediumFill } from '../../internal/icons';
import { getStyle, dialogClasses } from './style';
import { Button } from '../../Button';
import { ActionButton } from '../../Shared/ActionButton';
import { findNextFocusableElement } from '../../Shared/a11yUtils';
import { findAccessor } from '../../Shared/dom';
import { useTranslation } from '../../hooks/useTranslation';
import { ScrollView } from '../../ScrollView';
import useDevelopmentWarning from '../../hooks/useDevelopmentWarning';
import { useDOM } from '../../hooks/useDOM';
import { randomStr } from '../../Shared/random';
const DialogBase = (props) => {
    const { children, open, className, style, onClose, TransitionComponent, scroll, autoFocus = true, titleLabel, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, ...restProps } = props;
    // We do auto-focus only during the dialog's first focus and ignore the next focuses
    const firstFocusRef = useRef(true);
    function handleFocus(e) {
        if (autoFocus &&
            firstFocusRef.current &&
            e.target.classList.contains(dialogClasses.container)) {
            firstFocusRef.current = false;
            const candidate = findNextFocusableElement(e.target);
            if (candidate && findAccessor(candidate, (t) => t === e.target)) {
                candidate.focus();
            }
        }
    }
    useEffect(() => {
        if (!open) {
            firstFocusRef.current = true;
        }
    }, [open]);
    return (React.createElement(MuiDialog, { open: open, className: className, style: style, onClose: onClose, TransitionComponent: TransitionComponent, scroll: scroll, classes: {
            container: dialogClasses.container,
        }, PaperProps: {
            classes: {
                root: dialogClasses.paperRoot,
            },
            'aria-label': ariaLabel ?? titleLabel,
            'aria-labelledby': ariaLabelledBy,
        }, componentsProps: { backdrop: { className: dialogClasses.backdrop } }, onFocus: handleFocus, ...restProps }, children));
};
const Transition = React.forwardRef((props, ref) => React.createElement(Grow, { ref: ref, ...props }));
export const ConfirmDialog = (props) => {
    const { t } = useTranslation('Shared');
    const { open = true, className, zIndex, sx, width, title, content, children, footer, cancelText = t('cancel'), okText = t('ok'), onBackdropClick, onEscapeKeyDown, onOk, onCancel, autoFocusFooter, reverseButtons = false, okButtonProps, cancelButtonProps, closable = false, scroll, closeButtonProps, contentProps, titleProps, footerProps, disabledContentScrollView, ...restProps } = props;
    const refContentId = useRef(`dialog-content-${randomStr()}`);
    const listenCloseAction = (event, type) => {
        if (event && type === 'backdropClick') {
            onBackdropClick?.(event);
        }
        if (event && type === 'escapeKeyDown') {
            onEscapeKeyDown?.(event);
        }
    };
    const buttonList = [];
    if (cancelText !== null) {
        buttonList.push(React.createElement(ActionButton, { key: 0, type: "secondary", onClick: (e) => onCancel?.(e, { type: 'cancelButtonClick' }), "aria-label": typeof cancelText === 'string' ? cancelText : t('cancel'), ...cancelButtonProps }, cancelText));
    }
    if (okText !== null) {
        buttonList.push(React.createElement(ActionButton, { key: 1, onClick: (e) => onOk?.(e, { type: 'okButtonClick' }), autoFocus: autoFocusFooter === true, "aria-label": typeof okText === 'string' ? okText : t('ok'), ...okButtonProps }, okText));
    }
    if (reverseButtons) {
        buttonList.reverse();
    }
    const scrollerRef = useRef(null);
    const [scrollAtTop, setScrollAtTop] = useState(true);
    const [scrollAtBottom, setScrollAtBottom] = useState(true);
    const handleScroll = useCallback(() => {
        const scroller = scrollerRef.current;
        // istanbul ignore next
        if (!scroller) {
            return;
        }
        const { scrollHeight, scrollTop, clientHeight } = scroller;
        setScrollAtTop(scrollTop <= 0);
        setScrollAtBottom(scrollTop + clientHeight >= scrollHeight);
    }, []);
    const [contentMounted] = useDOM((contentElement) => {
        // istanbul ignore next
        const resizeObserver = window.ResizeObserver && new window.ResizeObserver(handleScroll);
        resizeObserver?.observe(contentElement);
        handleScroll();
        return () => {
            resizeObserver?.disconnect();
        };
    }, [handleScroll]);
    useDevelopmentWarning(
    /* istanbul ignore next */
    () => !!(content && children), '[Dialog] You have passed both `content` and `children` props to `<Dialog>`. They have the same meaning and are rendered at the same time. Please use only one of them, and children is preferred.');
    return (React.createElement(DialogBase, { className: clsx(dialogClasses.root, className, {
            [dialogClasses.scrollAtTop]: scrollAtTop,
            [dialogClasses.scrollAtBottom]: scrollAtBottom,
        }), scroll: scroll, open: open, style: {
            zIndex,
        }, sx: [
            (theme) => getStyle({ theme, scroll, width }),
            ...(Array.isArray(sx) ? sx : [sx]),
        ], onClose: listenCloseAction, TransitionComponent: Transition, titleLabel: typeof title === 'string' ? title : undefined, "aria-describedby": refContentId.current, ...restProps },
        title && (React.createElement(MuiDialogTitle, { classes: {
                root: clsx(dialogClasses.titleRoot, {
                    [dialogClasses.titleWithButton]: closable,
                }),
            }, ...titleProps }, title)),
        closable && (React.createElement(Button, { className: dialogClasses.closeIcon, onClick: (e) => onCancel?.(e, { type: 'closeButtonClick' }), variant: "icon", type: "tertiary", "aria-label": t('close'), size: "small", ...closeButtonProps },
            React.createElement(IconCloseMediumFill, null))),
        (content ?? children) ? (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        React.createElement(React.Fragment, null, disabledContentScrollView ? (React.createElement(MuiDialogContent, { id: refContentId.current, ...contentProps, classes: {
                root: dialogClasses.contentRoot,
            }, className: clsx(dialogClasses.disabledContentScrollView, contentProps?.className) },
            React.createElement("div", { className: dialogClasses.content },
                content,
                children))) : (React.createElement(ScrollView, { component: MuiDialogContent, id: refContentId.current, ...contentProps, classes: {
                root: dialogClasses.contentRoot,
            }, ref: contentMounted, scrollerProps: { ref: scrollerRef }, onScroll: handleScroll },
            React.createElement("div", { className: dialogClasses.content },
                content,
                children))))) : null,
        (footer ?? footer === null) ? (footer && (React.createElement(MuiDialogActions, { classes: {
                root: dialogClasses.footerRoot,
            }, ...footerProps }, footer))) : (React.createElement(MuiDialogActions, { classes: {
                root: dialogClasses.footerRoot,
            }, ...footerProps }, buttonList.map((Btn) => Btn)))));
};
